import { useCallback, useMemo, useState } from 'react';

import { Toggle } from '@/lib/base/forms/toggle';
import type {
  ChartProps,
  ViewComponent,
  ViewDefinition,
} from '@/types/dynamic_views';

import { ComponentRenderer } from '../dynamic-views/ComponentRenderer';

/**
 * Renders the manifest produced by `query_report` / `execute_saved_report`
 * inline in the conversation thread, immediately above the agent's text.
 *
 * The agent's tool result wraps its rows in a chart manifest. We unwrap the
 * single chart component, render it inside a titled card the full width of
 * the reply, and give the user a toggle to flip to a table view (which
 * re-uses the same ChartRenderer with `chart_type: "table"`).
 *
 * Mounting in the main thread (rather than only in the Reasoning side
 * panel's `ToolUseItem`) is what makes the chart actually visible to users.
 */
interface InlineReportChartProps {
  manifest: ViewDefinition;
}

const FALLBACK_TITLE = 'Report results';

export const InlineReportChart: React.FC<InlineReportChartProps> = ({
  manifest,
}) => {
  const [showTable, setShowTable] = useState(false);

  const handleToggle = useCallback((selected: boolean) => {
    setShowTable(selected);
  }, []);

  // Find the first chart component in the manifest. Today the backend only
  // emits a single component per manifest, so this is exact.
  const chartComponent = useMemo<ViewComponent | null>(() => {
    const components = manifest?.components ?? [];
    return components.find(c => c.type === 'chart') ?? null;
  }, [manifest]);

  // Derive a render-time component that respects the toggle by overriding
  // chart_type when the user has chosen the table view.
  const renderedComponent = useMemo<ViewComponent | null>(() => {
    if (!chartComponent) {
      return null;
    }
    if (!showTable) {
      return chartComponent;
    }
    const props = chartComponent.props as ChartProps;
    return {
      ...chartComponent,
      props: { ...props, chart_type: 'table' },
    };
  }, [chartComponent, showTable]);

  if (!chartComponent || !renderedComponent) {
    return null;
  }

  const title =
    (chartComponent.props as ChartProps).title?.trim() || FALLBACK_TITLE;

  return (
    // Chat-module card canon (bg-bg-primary + border + rounded-xl) rather
    // than the base Card's `card-refined` utility — card-refined hardcodes a
    // white background that fights dark mode and doesn't match the other
    // inline modules (proposal cards, dynamic-view cards).
    <div className='w-full overflow-hidden bg-bg-primary border border-border-secondary rounded-xl'>
      <div className='flex items-center justify-between gap-3 px-4 py-3 border-b border-border-secondary'>
        <h3 className='text-sm font-medium text-fg-primary truncate'>
          {title}
        </h3>
        <Toggle
          size='sm'
          slim
          label='Table view'
          isSelected={showTable}
          onChange={handleToggle}
          aria-label='Show as table'
        />
      </div>
      <div className='p-4 h-[400px] overflow-auto'>
        <ComponentRenderer component={renderedComponent} />
      </div>
    </div>
  );
};

export default InlineReportChart;
